import { apiClient, unwrap } from '@services/api';
import type { Conversation, InboxItem, Message, MessageThreadResult } from '@/types';

const markReadTimers = new Map<string, ReturnType<typeof setTimeout>>();

export const messagingService = {
  async getInbox(): Promise<InboxItem[]> {
    const res = await apiClient.get<InboxItem[]>('/messages/inbox');
    return unwrap(res);
  },

  async getConversations(): Promise<Conversation[]> {
    const res = await apiClient.get<Conversation[]>('/messages/conversations');
    return unwrap(res);
  },

  async getPartnerMessages(partnerId: string, cursor?: string): Promise<MessageThreadResult> {
    const res = await apiClient.get<MessageThreadResult>(`/messages/partner/${partnerId}`, {
      params: cursor ? { cursor } : undefined,
    });
    return unwrap(res);
  },

  async getGeneralMessages(partnerId: string, cursor?: string): Promise<MessageThreadResult> {
    const res = await apiClient.get<MessageThreadResult>(`/messages/general/${partnerId}`, {
      params: cursor ? { cursor } : undefined,
    });
    return unwrap(res);
  },

  async getOrderMessages(orderId: string, cursor?: string): Promise<MessageThreadResult> {
    const res = await apiClient.get<MessageThreadResult>(`/messages/order/${orderId}`, {
      params: cursor ? { cursor } : undefined,
    });
    return unwrap(res);
  },

  async sendMessage(
    receiverId: string,
    content: string,
    orderId?: string,
    clientMessageId?: string,
  ): Promise<Message> {
    const res = await apiClient.post<{ message: Message }>('/messages/send', {
      receiverId,
      content,
      orderId,
      clientMessageId,
    });
    return unwrap(res).message;
  },

  async markRead(payload: { partnerId?: string; orderId?: string }): Promise<void> {
    await apiClient.post('/messages/read', payload);
  },

  /** Debounced mark-read to avoid rate limits when many messages arrive quickly. */
  scheduleMarkRead(
    payload: { partnerId?: string; orderId?: string },
    delayMs = 1000,
  ): Promise<void> {
    const key = payload.orderId ?? payload.partnerId;
    if (!key) return Promise.resolve();

    const existing = markReadTimers.get(key);
    if (existing) clearTimeout(existing);

    return new Promise((resolve) => {
      markReadTimers.set(
        key,
        setTimeout(() => {
          markReadTimers.delete(key);
          void messagingService
            .markRead(payload)
            .then(() => resolve())
            .catch(() => resolve());
        }, delayMs),
      );
    });
  },

  async getUnreadCount(): Promise<{ count: number }> {
    const res = await apiClient.get<{ count: number }>('/messages/unread-count');
    return unwrap(res);
  },

  async getUnreadOrders(): Promise<Array<{ orderId: string; count: number }>> {
    const res = await apiClient.get<Array<{ orderId: string; count: number }>>(
      '/messages/unread-orders',
    );
    return unwrap(res);
  },
};
